/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * CalendarWidget.tsx — locale-aware meeting scheduler for the operator workspace.
 * It captures a date and time, presents conflict alternatives, and commits a
 * validated draft to the shared call workspace. Provider-side booking and
 * calendar synchronization remain outside this presentation component.
 */

import { useWorkspace, useOperatorStore } from '@/store';
import { Panel, Button } from '@/components/ui';
import { HighlightBox } from '@/features/copilot';
import {
  toInputDate,
  toScenarioDate,
  generateTimeSlots,
  DEFAULT_SLOT_START,
  DEFAULT_SLOT_END,
  DEFAULT_SLOT_STEP,
} from './calendarUtils';
import type { FieldSource } from '@/store';
import { useTranslation } from '@/i18n';

/**
 * E5-S1: Calendar scheduling widget. Plan: 14 wireframes (Screen 05).
 * Date selector, time slots, conflict banner, confirm meeting.
 */
export function CalendarWidget() {
  const t = useTranslation();
  const workspace = useWorkspace();
  const setScenarioField = useOperatorStore((s) => s.setScenarioField);
  const clearMeetingConflict = useOperatorStore((s) => s.clearMeetingConflict);
  const confirmMeeting = useOperatorStore((s) => s.confirmMeeting);

  const dateValue = workspace.scenarioFields.date?.value ?? '';
  const timeValue = workspace.scenarioFields.time?.value ?? '';
  const conflict = workspace.meetingConflict;
  const confirmedAt = workspace.meetingConfirmedAt;

  const inputDate = toInputDate(dateValue);

  const slots = generateTimeSlots(DEFAULT_SLOT_START, DEFAULT_SLOT_END, DEFAULT_SLOT_STEP);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (!v) {
      setScenarioField('date', '', 'manual');
      return;
    }
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) {
      setScenarioField('date', toScenarioDate(d), 'manual');
    }
  };

  const handleSlotSelect = (slot: string, source: FieldSource = 'manual') => {
    setScenarioField('time', slot, source);
  };

  const hasDraft = dateValue.trim() !== '' && timeValue.trim() !== '';
  const canConfirm = hasDraft && !confirmedAt;

  return (
    <HighlightBox widgetId="calendar_widget">
      <Panel title={t('calendar.title')} padding="md">
        <div className="space-y-4">
          <div>
            <label
              htmlFor="meeting-date"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              {t('calendar.date')}
            </label>
            <input
              id="meeting-date"
              type="date"
              value={inputDate}
              onChange={handleDateChange}
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--toggle-bg)',
                color: 'var(--text-primary)',
              }}
              aria-describedby={conflict ? 'meeting-conflict-message' : undefined}
            />
          </div>

          <div>
            <span
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--text-secondary)' }}
            >
              {t('calendar.time')}
            </span>
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label={t('calendar.time_selection')}
            >
              {slots.map((slot) => {
                const selected = timeValue === slot;
                return (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => handleSlotSelect(slot)}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                      selected ? 'bg-blue-600 text-white' : ''
                    }`}
                    style={
                      selected
                        ? {}
                        : { background: 'var(--toggle-bg)', color: 'var(--text-secondary)' }
                    }
                    aria-pressed={selected}
                  >
                    {slot}
                  </button>
                );
              })}
            </div>
          </div>

          {conflict ? (
            <div
              id="meeting-conflict-message"
              className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200"
              role="alert"
            >
              <p className="font-medium">
                {t('calendar.conflict_message', { message: conflict.message })}
              </p>
              {conflict.alternatives && conflict.alternatives.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {conflict.alternatives.map((alt) => (
                    <Button
                      key={alt}
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        handleSlotSelect(alt, 'manual');
                        clearMeetingConflict();
                      }}
                    >
                      {alt}
                    </Button>
                  ))}
                </div>
              )}
              <div className="mt-2">
                <Button variant="ghost" size="sm" onClick={() => clearMeetingConflict()}>
                  {t('common.close')}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {t('calendar.no_conflict')}
            </p>
          )}

          {confirmedAt && (
            <p className="text-sm text-green-600 dark:text-green-400 font-medium">
              {t('calendar.confirmed')}
            </p>
          )}

          <div>
            <Button
              variant="primary"
              onClick={() => confirmMeeting()}
              disabled={!canConfirm}
              aria-disabled={!canConfirm}
            >
              {t('calendar.confirm')}
            </Button>
          </div>
        </div>
      </Panel>
    </HighlightBox>
  );
}
